import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import {
  FormGroup,
  FormControl,
  Validators,
  FormArray,
  ReactiveFormsModule,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { Subject, takeUntil } from "rxjs";

import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatRadioModule } from "@angular/material/radio";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatDividerModule } from "@angular/material/divider";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatTooltipModule } from "@angular/material/tooltip";

import {
  AssetConfigService,
  MasterType,
  SITES,
  BEARING_NUMBERS,
  ODR_OPTIONS,
  ASSET_TYPES,
} from "./single-edit.service";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatSnackBarModule,
    MatTooltipModule,
  ],
  selector: "app-piezo-single-edit",
  templateUrl: "./piezo-single-edit.component.html",
  styleUrl: "./piezo-single-edit.component.scss",
})
export class PiezoSingleSensorEditComponent
  implements OnInit, OnDestroy, OnChanges
{
  /**
   * master_type drives the entire form shape:
   *   4  → Piezo device   (no push-rate / network sections)
   *   1  → EM-Mini        (push-rate + network; RTD2/3 disabled)
   *   2  → EM-Standard    (push-rate + network; RTD2/3 disabled)
   *   3  → EM-Std + RTDs  (push-rate + network; all RTDs enabled)
   */
  masterType: MasterType = null;

  /** Asset type controls which sub-sections are visible */
  assetType: string = "MOTOR";

  // ── Constants exposed to template ──────────────────────────────────────────
  readonly SITES = SITES;
  readonly BEARING_NUMBERS = BEARING_NUMBERS;
  readonly ODR_OPTIONS = ODR_OPTIONS;
  readonly ASSET_TYPES = ASSET_TYPES;
  readonly INSULATION_CLASSES = ["A", "B", "F", "H"];
  readonly GMF_COUNT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

  // ── UI State ───────────────────────────────────────────────────────────────
  loading = true;
  hidePassword = true;
  applications: { id: string; name: string }[] = [];
  sites: any = [];
  device_inventory: any = [];
  bearing_numbers: any = [];
  initial_fd: any = {};
  sensor: any = {};
  save_loader=false;

  // ── Computed helpers ────────────────────────────────────────────────────────
  get isPiezo(): boolean {
    return this.masterType === 4;
  }
  get isEmsaver(): boolean {
    return this.masterType !== 4;
  }
  get isGear(): boolean {
    return this.assetType === "GEAR";
  }
  get isMotor(): boolean {
    return this.assetType === "MOTOR";
  }
  get isPump(): boolean {
    return this.assetType === "PUMP";
  }
  get isVariableRpm(): boolean {
    return (
      this.form.get("configuration_details.rated_rpm_type")?.value ===
      "variable"
    );
  }
  get isPulleyDrive(): boolean {
    return this.form.get("drive_details.drive_type")?.value === "pulley";
  }
  get gmfEnabled(): boolean {
    return (
      this.form.get("configuration_details.gmf_details.is_gmf")?.value ===
      "gmf_enabled"
    );
  }
  get masterLabel(): string {
    return (
      { 4: "Piezo", 1: "EM-Mini", 2: "EM-Standard", 3: "EM-Std + RTDs" }[
        this.masterType
      ] ?? ""
    );
  }

  /**
   * RTD-2 and RTD-3 are disabled for EM-Mini (1) and EM-Standard (2).
   * Only EM-Std+RTDs (3) and Piezo (4) allow all three RTDs.
   */
  get rtd2Disabled(): boolean {
    return this.isEmsaver && this.masterType !== 3;
  }
  get rtd3Disabled(): boolean {
    return this.isEmsaver && this.masterType !== 3;
  }

  /** Bearing number dropdowns: disabled when sleeve_bearing is selected */
  get bearingDisabled(): boolean {
    return (
      this.form.get("configuration_details.bearing_type")?.value ===
      "sleeve_bearing"
    );
  }
  get pinionBearingDisabled(): boolean {
    return (
      this.form.get("configuration_details.pinion_gear_details.p_bearing_type")
        ?.value === "sleeve_bearing"
    );
  }
  get outputBearingDisabled(): boolean {
    return (
      this.form.get("configuration_details.output_gear_details.o_bearing_type")
        ?.value === "sleeve_bearing"
    );
  }

  // ── Form ────────────────────────────────────────────────────────────────────
  form!: FormGroup;

  private destroy$ = new Subject<void>();

  constructor(
    private assetConfigService: AssetConfigService,
    private snackBar: MatSnackBar,
    private router: ActivatedRoute,
    private rtr: Router,
  ) {}

  ngOnInit(): void {
    this.router.queryParamMap.subscribe((params) => {
      this.sensor = JSON.parse(params.get("sensor") || "{}");
      this.assetType = this.sensor.assetType;
      this.masterType = parseInt(this.sensor.master_type);
      this.buildForm();
      Promise.all([
        this.assetConfigService.fetchSites(),
        this.assetConfigService.fetchInventory(),
        this.assetConfigService.fetchBearingNumbers(),
      ]).then(([sites, inv, bearings]) => {
        this.sites = sites;
        this.device_inventory = inv;
        this.bearing_numbers = bearings;
        this.loadFormData();
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["masterType"] && !changes["masterType"].firstChange) {
      //   this.loadFormData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Form Builder ─────────────────────────────────────────────────────────────
  buildForm(): void {
    this.form = new FormGroup({
      general_details: new FormGroup({
        asset_name: new FormControl(
          { value: "", disabled: true },
          Validators.required,
        ),
        site: new FormControl(
          { value: "", disabled: true },
          Validators.required,
        ),
        application: new FormControl(
          { value: "", disabled: true },
          Validators.required,
        ),
      }),

      configuration_details: new FormGroup({
        line_current: new FormControl(0),
        device_name: new FormControl("", Validators.required),
        mac_address: new FormControl("", Validators.required),
        motor_make: new FormControl("", Validators.required),
        equipment_location: new FormControl(""),
        frame: new FormControl(""),
        rated_amb_temp: new FormControl(0),
        rated_amb_temp_unit: new FormControl("C"),
        insulation_class: new FormControl("", Validators.required),
        serial_number: new FormControl(
          { value: "", disabled: true },
          Validators.required,
        ),
        rated_power: new FormControl(0, [
          Validators.required,
          Validators.min(1),
        ]),
        rated_power_unit: new FormControl("KW", Validators.required),
        rated_rpm: new FormControl(0, [Validators.required, Validators.min(1)]),
        rated_rpm_type: new FormControl("fixed", Validators.required),
        rated_voltage: new FormControl(0),
        percentage_eff: new FormControl(0),
        line_freq: new FormControl("50", Validators.required),
        power_factor: new FormControl(""),
        duty: new FormControl(""),
        bearing_type: new FormControl("ball_bearing"),
        de_number: new FormControl(""),
        nde_number: new FormControl(""),
        pole_pairs: new FormControl(""),

        pinion_gear_details: new FormGroup({
          no_of_stages: new FormControl(""),
          p_gear_rpm: new FormControl(""),
          p_gear_teeth: new FormControl(""),
          p_bearing_type: new FormControl("ball_bearing"),
          p_deb_no: new FormControl({ value: "NA", disabled: true }),
          p_ndeb_no: new FormControl({ value: "NA", disabled: true }),
        }),

        output_gear_details: new FormGroup({
          no_of_opt_gears: new FormControl(""),
          o_gear_rpm: new FormControl(""),
          o_gear_teeth: new FormControl(""),
          o_bearing_type: new FormControl("ball_bearing"),
          o_deb_no: new FormControl({ value: "NA", disabled: true }),
          o_ndeb_no: new FormControl({ value: "NA", disabled: true }),
        }),

        gmf_details: new FormGroup({
          is_gmf: new FormControl("gmf_disabled"),
          no_of_gmf: new FormControl(""),
          gmf_list: new FormArray([]),
        }),
      }),

      drive_details: new FormGroup({
        drive_type: new FormControl("direct"),
        driver_pulley_diameter: new FormControl(""),
        driven_pulley_diameter: new FormControl(""),
        dist_bt_shaft: new FormControl(""),
        belt_no: new FormControl(""),
        belt_length: new FormControl(""),
      }),

      other_configurations: new FormGroup({
        sensor_mounting: new FormControl("PARALLEL_SIDE"),
        RTD_1: new FormControl("MS"),
        RTD_2: new FormControl({ value: "na", disabled: true }),
        RTD_3: new FormControl({ value: "na", disabled: true }),
        temp_1_comp: new FormControl({ value: "0", disabled: false }),
        temp_2_comp: new FormControl({ value: "0", disabled: false }),
        temp_3_comp: new FormControl({ value: "0", disabled: false }),
        vib_1_comp: new FormControl({ value: "1", disabled: false }),
        vib_2_comp: new FormControl({ value: "1", disabled: false }),
        vib_3_comp: new FormControl({ value: "1", disabled: false }),
      }),

      // Always included; section visibility controlled by isPiezo/isEmsaver
      device_push_rate_configuration: new FormGroup({
        summaryFileRefreshRate: new FormControl(15),
        fullRefreshFileRate: new FormControl(60),
        scanRate: new FormControl(15),
        odr_normal: new FormControl("Normal"),
        odr_high_res: new FormControl("Normal"),
        anomalyAnalysisTime: new FormControl(180),
        anomalyCounter: new FormControl(1),
        machine_threshold_status: new FormControl(0.5),
      }),

      network_configuration: new FormGroup({
        connectivity_type: new FormControl("Wifi"),
        wifi_ssid: new FormControl(""),
        wifi_password: new FormControl(""),
      }),
    });

    this.subscribeToFormChanges();
  }

  // ── Data Loader ──────────────────────────────────────────────────────────────
  loadFormData(): void {
    this.loading = true;
    this.assetConfigService
      .fetchFormDataV2({
        sid: this.router.snapshot.queryParamMap.get("sid") || "",
        aid: JSON.parse(
          this.router.snapshot.queryParamMap.get("sensor") || "{}",
        ).asset_id,
      })
      .then((data) => {
        this.initial_fd = { ...data };
        this.form.patchValue(data);
        this.onAssetTypeChange(this.assetType);
        this.applyRtdState();
        this.applyBearingState();
        this.applyGearSectionState();
        this.form
          .get("configuration_details.mac_address")
          ?.disable({ emitEvent: false });
        this.form
          .get("configuration_details.mac_address")
          ?.patchValue(data.configuration_details.mac_address.id);
        this.device_inventory = [
          ...this.device_inventory,
          data.configuration_details.mac_address,
        ];
        this.setApplicationsForSite(data.general_details.site);
        this.loading = false;
        console.log(this.form.getRawValue());

        if (this.sensor.assetMarker == "MASTER") {
          this.form.get("general_details.site").enable();
          this.form.get("general_details.application").enable();
        }
      });
  }

  // ── Internal Subscriptions ───────────────────────────────────────────────────
  private subscribeToFormChanges(): void {
    // RPM type toggle
    this.form
      .get("configuration_details.rated_rpm_type")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((type) => this.onRpmTypeChange(type));

    // Bearing type toggle (non-gear)
    this.form
      .get("configuration_details.bearing_type")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((type) => this.applyBearingState(type));

    // Pinion bearing toggle
    this.form
      .get("configuration_details.pinion_gear_details.p_bearing_type")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((type) => this.applyPinionBearingState(type));

    // Output bearing toggle
    this.form
      .get("configuration_details.output_gear_details.o_bearing_type")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((type) => this.applyOutputBearingState(type));

    // GMF count changes
    this.form
      .get("configuration_details.gmf_details.no_of_gmf")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncGmfList());

    this.form
      .get("configuration_details.gmf_details.is_gmf")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        if (val === "gmf_disabled") {
          this.gmfList.clear();
          this.form
            .get("configuration_details.gmf_details.no_of_gmf")
            ?.setValue("", { emitEvent: false });
        }
      });

    // Site change → populate applications
    this.form
      .get("general_details.site")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((siteId) => this.setApplicationsForSite(siteId));
  }

  // ── Asset Type Change ────────────────────────────────────────────────────────
  onAssetTypeChange(type: string): void {
    this.assetType = type;
    this.applyGearSectionState();
  }

  private applyGearSectionState(): void {
    const cfg = this.form.get("configuration_details") as FormGroup;
    const pinion = cfg.get("pinion_gear_details") as FormGroup;
    const output = cfg.get("output_gear_details") as FormGroup;
    const gmf = cfg.get("gmf_details") as FormGroup;
    const drive = this.form.get("drive_details") as FormGroup;

    if (this.isGear) {
      pinion.enable({ emitEvent: false });
      output.enable({ emitEvent: false });
      gmf.enable({ emitEvent: false });
      drive.disable({ emitEvent: false });
      cfg.get("bearing_type")?.disable({ emitEvent: false });
      cfg.get("de_number")?.disable({ emitEvent: false });
      cfg.get("nde_number")?.disable({ emitEvent: false });
      // Re-apply pinion/output bearing sub-state
      this.applyPinionBearingState(pinion.get("p_bearing_type")?.value);
      this.applyOutputBearingState(output.get("o_bearing_type")?.value);

      pinion.get("p_gear_rpm").setValidators(Validators.required);
      output.get("o_gear_rpm").setValidators(Validators.required);
      output.get("no_of_opt_gears").setValidators(Validators.required);
    } else {
      pinion.disable({ emitEvent: false });
      output.disable({ emitEvent: false });
      gmf.disable({ emitEvent: false });
      drive.enable({ emitEvent: false });
      cfg.get("bearing_type")?.enable({ emitEvent: false });
      this.applyBearingState(cfg.get("bearing_type")?.value);
    }
  }

  // ── RTD State ────────────────────────────────────────────────────────────────
  private applyRtdState(): void {
    const other = this.form.get("other_configurations") as FormGroup;
    const allowAll = this.isEmsaver && this.masterType === 3;
    console.log(
      `THIS>EMSAVER:${this.isEmsaver} ||  MASTERTYPE-->${this.masterType} || ALLOWALL--> ${allowAll}`,
    );
    if (allowAll) {
      other.get("RTD_2")?.enable({ emitEvent: false });
      other.get("RTD_3")?.enable({ emitEvent: false });
      other.get("temp_2_comp")?.enable({ emitEvent: false });
      other.get("temp_3_comp")?.enable({ emitEvent: false });
    } else {
      other.get("RTD_2")?.disable({ emitEvent: false });
      other.get("RTD_3")?.disable({ emitEvent: false });
      other.get("temp_2_comp")?.disable({ emitEvent: false });
      other.get("temp_3_comp")?.disable({ emitEvent: false });
    }
  }

  // ── Bearing State Helpers ────────────────────────────────────────────────────
  private applyBearingState(type?: string): void {
    console.log(`TYPE RECEIVED: ${type}`);
    const cfg = this.form.get("configuration_details") as FormGroup;
    const isSleeve =
      (type ?? cfg.get("bearing_type")?.value) === "sleeve_bearing";
    console.log(`Bearing type : ${isSleeve}`);
    if (isSleeve) {
      cfg.get("de_number")?.setValue("NA", { emitEvent: false });
      cfg.get("de_number")?.disable({ emitEvent: false });
      cfg.get("nde_number")?.setValue("NA", { emitEvent: false });
      cfg.get("nde_number")?.disable({ emitEvent: false });
    } else {
      cfg.get("de_number")?.enable({ emitEvent: false });
      cfg.get("nde_number")?.enable({ emitEvent: false });
      cfg.get("de_number")?.setValidators(Validators.required);
      cfg.get("nde_number")?.setValidators(Validators.required);
      console.log(this.initial_fd.configuration_details)
      cfg
        .get("de_number")
        ?.setValue(cfg.get("de_number")?.value == "NA" ? "1200" : cfg.get("de_number")?.value, {
          emitEvent: false,
        });
      cfg
        .get("nde_number")
        ?.setValue(cfg.get("nde_number")?.value == "NA" ? "1200" : cfg.get("nde_number")?.value, {
          emitEvent: false,
        });
    }
    cfg.get("de_number")?.updateValueAndValidity();
    cfg.get("nde_number")?.updateValueAndValidity();
  }

  private applyPinionBearingState(type?: string): void {
    const pinion = this.form.get(
      "configuration_details.pinion_gear_details",
    ) as FormGroup;
    const isSleeve = type === "sleeve_bearing";
    if (isSleeve) {
      pinion.get("p_deb_no")?.setValue("NA", { emitEvent: false });
      pinion.get("p_deb_no")?.disable({ emitEvent: false });
      pinion.get("p_ndeb_no")?.setValue("NA", { emitEvent: false });
      pinion.get("p_ndeb_no")?.disable({ emitEvent: false });
      pinion.get("p_ndeb_no")?.clearValidators();
      pinion.get("p_deb_no")?.clearValidators();
    } else {
      pinion.get("p_deb_no")?.enable({ emitEvent: false });
      pinion.get("p_ndeb_no")?.enable({ emitEvent: false });
      pinion.get("p_ndeb_no")?.setValidators(Validators.required);
      pinion.get("p_deb_no")?.setValidators(Validators.required);

      pinion
        .get("p_deb_no")
        ?.setValue(pinion.get("p_deb_no")?.value == "NA" ? "1200" : pinion.get("p_deb_no")?.value,
          { emitEvent: false },
        );
      pinion
        .get("p_ndeb_no")
        ?.setValue(pinion.get("p_ndeb_no")?.value == "NA" ? "1200" : pinion.get("p_ndeb_no")?.value,
          { emitEvent: false },
        );
    }

    pinion.get("p_ndeb_no")?.updateValueAndValidity();
    pinion.get("p_deb_no")?.updateValueAndValidity();
  }

  private applyOutputBearingState(type?: string): void {
    const output = this.form.get(
      "configuration_details.output_gear_details",
    ) as FormGroup;
    const isSleeve = type === "sleeve_bearing";
    if (isSleeve) {
      output.get("o_deb_no")?.setValue("NA", { emitEvent: false });
      output.get("o_deb_no")?.disable({ emitEvent: false });
      output.get("o_ndeb_no")?.setValue("NA", { emitEvent: false });
      output.get("o_ndeb_no")?.disable({ emitEvent: false });
      output.get("o_ndeb_no")?.clearValidators();
      output.get("o_deb_no")?.clearValidators();
    } else {
      output.get("o_deb_no")?.enable({ emitEvent: false });
      output.get("o_ndeb_no")?.enable({ emitEvent: false });
      output.get("o_ndeb_no")?.setValidators(Validators.required);
      output.get("o_deb_no")?.setValidators(Validators.required);

      output
        .get("o_deb_no")
        ?.setValue(
          this.initial_fd?.configuration_details?.output_gear_details
            ?.o_deb_no == "NA" ? "1200" : this.initial_fd?.configuration_details?.output_gear_details?.o_deb_no || "0",
          { emitEvent: false },
        );
      output
        .get("o_ndeb_no")
        ?.setValue(
          this.initial_fd?.configuration_details?.output_gear_details
            ?.o_ndeb_no == "NA" ? "1200" : this.initial_fd?.configuration_details?.output_gear_details?.o_ndeb_no || "0",
          { emitEvent: false },
        );
    }
    output.get("o_ndeb_no")?.updateValueAndValidity();
    output.get("o_deb_no")?.updateValueAndValidity();
  }

  // ── RPM Type ─────────────────────────────────────────────────────────────────
  private onRpmTypeChange(type: string): void {
    const cfg = this.form.get("configuration_details") as FormGroup;
    if (type === "variable") {
      if (!cfg.get("min_rpm"))
        cfg.addControl("min_rpm", new FormControl(0, Validators.required));
      if (!cfg.get("max_rpm"))
        cfg.addControl("max_rpm", new FormControl(0, Validators.required));
    } else {
      cfg.removeControl("min_rpm");
      cfg.removeControl("max_rpm");
    }
  }

  // ── GMF List ─────────────────────────────────────────────────────────────────
  get gmfList(): FormArray {
    return this.form.get(
      "configuration_details.gmf_details.gmf_list",
    ) as FormArray;
  }

  private syncGmfList(): void {
    const count =
      Number(
        this.form.get("configuration_details.gmf_details.no_of_gmf")?.value,
      ) || 0;
    const current = this.gmfList.length;
    if (count > current) {
      for (let i = current; i < count; i++) {
        this.gmfList.push(
          new FormGroup({
            gmf_value: new FormControl("", Validators.required),
            number_for_gmf: new FormControl("", Validators.required),
          }),
        );
      }
    } else if (count < current) {
      for (let i = current - 1; i >= count; i--) {
        this.gmfList.removeAt(i);
      }
    }
  }

  // ── Site / Application ───────────────────────────────────────────────────────
  setApplicationsForSite(siteId: string): void {
    this.applications =
      this.sites.find((s) => s.id == siteId)?.applications ?? [];
  }

  // ── Form Actions ─────────────────────────────────────────────────────────────
  saveForm(): void {
    this.save_loader = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.snackBar.open(
        "Please fix validation errors before saving.",
        "Close",
        { duration: 3000 },
      );
      this.logInvalidControls(this.form);
      this.save_loader = false
      return;
    }
    const payload = this.form.getRawValue();

    if (this.masterType == 4) {
      delete payload.device_push_rate_configuration;
      delete payload.network_configuration;
    }

    if (this.assetType !== "GEAR") {
      delete payload.configuration_details.pinion_gear_details;
      delete payload.configuration_details.output_gear_details;
    }

    payload.sensor_details = this.sensor;
    this.assetConfigService.saveFormData(payload).then((result) => {
      if (result.code == 200) {
        this.snackBar.open("Configuration saved successfully!", "Close", {
          duration: 3000,
        });
        this.rtr.navigate(["/piezo-dashboard"]);
      } else
        this.snackBar.open("Failed to save configuration.", "Close", {
          duration: 3000,
        });
    }).catch((err)=>{
      console.error("Save error:", err);
      this.snackBar.open("An error occurred while saving.", "Close", {
        duration: 3000,
      });
    }).finally(()=>{
      this.save_loader = false;
    });
  }

  resetForm(): void {
    this.loadFormData();
    this.snackBar.open("Form reset to last saved values.", "Close", {
      duration: 2000,
    });
  }

  private logInvalidControls(group: FormGroup | FormArray, path = ""): void {
    Object.entries((group as any).controls).forEach(([key, ctrl]: any) => {
      const fullPath = path ? `${path}.${key}` : key;
      if (ctrl instanceof FormGroup || ctrl instanceof FormArray) {
        this.logInvalidControls(ctrl, fullPath);
      } else if (ctrl.invalid) {
        console.warn(`❌ Invalid: ${fullPath}`, ctrl.errors);
      }
    });
  }

  /** Expose FormArray controls to template for *ngFor */
  get gmfControls() {
    return (
      this.form.get("configuration_details.gmf_details.gmf_list") as FormArray
    ).controls;
  }

  goBack() {
    this.rtr.navigate(["/piezo-dashboard"]);
  }
}
